import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Link2, Settings, RefreshCw, Users, FileText, Download, Upload,
  CheckCircle, XCircle, ExternalLink, ArrowRightLeft, AlertTriangle
} from "lucide-react";
import axios from "axios";

const FortnoxIntegration = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  
  const [formData, setFormData] = useState({
    client_id: "",
    client_secret: "",
    redirect_uri: ""
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/fortnox/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
      if (response.data.client_id) {
        setFormData(prev => ({
          ...prev,
          client_id: response.data.client_id || "",
          redirect_uri: response.data.redirect_uri || ""
        }));
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!formData.client_id || !formData.client_secret) {
      toast.error("Fyll i Client ID och Client Secret");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/fortnox/config`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Fortnox-konfiguration sparad!");
      fetchConfig();
    } catch (error) {
      toast.error("Kunde inte spara konfiguration");
    } finally {
      setSaving(false);
    }
  };

  const handleAuthorize = async () => {
    try {
      const response = await axios.get(`${API}/fortnox/authorize`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open Fortnox authorization in new window
      window.open(response.data.authorization_url, "_blank");
      toast.info("Logga in i Fortnox och godkänn behörigheter");
    } catch (error) {
      toast.error("Kunde inte starta auktorisering");
    }
  };

  const handleSyncCustomers = async (direction) => {
    setSyncing("customers_" + direction);
    try {
      const endpoint = direction === "export" 
        ? `${API}/fortnox/sync/customers`
        : `${API}/fortnox/import/customers`;
      
      const response = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} fel uppstod`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Synkronisering misslyckades";
      toast.error(detail);
    } finally {
      setSyncing(null);
    }
  };

  const handleSyncInvoices = async () => {
    setSyncing("invoices");
    try {
      const response = await axios.post(`${API}/fortnox/sync/invoices`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} fel uppstod`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Synkronisering misslyckades";
      toast.error(detail);
    } finally {
      setSyncing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="fortnox-integration">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Fortnox Integration</h1>
          <p className="text-slate-500 mt-1">Synkronisera kunder, fakturor och bokföring med Fortnox</p>
        </div>
        <a 
          href="https://developer.fortnox.se" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700"
        >
          <ExternalLink size={16} />
          Fortnox Developer Portal
        </a>
      </div>

      <Tabs defaultValue="config" className="space-y-6">
        <TabsList>
          <TabsTrigger value="config">Konfiguration</TabsTrigger>
          <TabsTrigger value="sync" disabled={!config?.is_connected}>Synkronisering</TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-6">
          {/* Connection Status */}
          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${config?.is_connected ? 'bg-green-100' : 'bg-slate-100'}`}>
                    <Link2 className={`h-6 w-6 ${config?.is_connected ? 'text-green-600' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {config?.is_connected ? "Ansluten till Fortnox" : "Ej ansluten"}
                    </p>
                    <p className="text-sm text-slate-500">
                      {config?.is_connected 
                        ? `Ansluten sedan ${new Date(config.connected_at).toLocaleDateString("sv-SE")}`
                        : "Konfigurera och anslut för att synkronisera data"}
                    </p>
                  </div>
                </div>
                
                <Badge className={config?.is_connected ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                  {config?.is_connected ? (
                    <><CheckCircle size={14} className="mr-1" /> Ansluten</>
                  ) : (
                    <><XCircle size={14} className="mr-1" /> Ej ansluten</>
                  )}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* API Configuration */}
          <Card className="card-base" data-testid="fortnox-config">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                API-konfiguration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client ID *</Label>
                  <Input 
                    value={formData.client_id}
                    onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                    placeholder="Från Fortnox Developer Portal"
                    data-testid="fortnox-client-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Secret *</Label>
                  <Input 
                    type="password"
                    value={formData.client_secret}
                    onChange={(e) => setFormData({...formData, client_secret: e.target.value})}
                    placeholder="••••••••"
                    data-testid="fortnox-client-secret"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={handleSaveConfig}
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-fortnox-config"
                >
                  {saving ? "Sparar..." : "Spara konfiguration"}
                </Button>
                
                {config?.is_configured && !config?.is_connected && (
                  <Button 
                    onClick={handleAuthorize}
                    variant="outline"
                    data-testid="authorize-fortnox"
                  >
                    <Link2 className="mr-2 h-4 w-4" />
                    Anslut till Fortnox
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Setup Guide */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Så här ansluter du till Fortnox</p>
                  <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Gå till <a href="https://developer.fortnox.se" target="_blank" rel="noopener noreferrer" className="underline">Fortnox Developer Portal</a></li>
                    <li>Skapa en ny applikation</li>
                    <li>Kopiera Client ID och Client Secret</li>
                    <li>Klistra in ovan och spara</li>
                    <li>Klicka "Anslut till Fortnox" och godkänn behörigheter</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Tab */}
        <TabsContent value="sync" className="space-y-6">
          {/* Customers Sync */}
          <Card className="card-base" data-testid="sync-customers">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Kunder
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={() => handleSyncCustomers("export")}
                  disabled={!!syncing}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {syncing === "customers_export" ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Exportera till Fortnox
                </Button>
                
                <Button 
                  onClick={() => handleSyncCustomers("import")}
                  disabled={!!syncing}
                  variant="outline"
                >
                  {syncing === "customers_import" ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Importera från Fortnox
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-3">
                Synkronisera kundregister mellan Yubi och Fortnox
              </p>
            </CardContent>
          </Card>

          {/* Invoices Sync */}
          <Card className="card-base" data-testid="sync-invoices">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Fakturor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleSyncInvoices}
                  disabled={!!syncing}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {syncing === "invoices" ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Exportera fakturor till Fortnox
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-3">
                Skicka skickade/betalda fakturor till Fortnox. Kunder måste vara synkade först.
              </p>
            </CardContent>
          </Card>

          {/* Sync Status Info */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <ArrowRightLeft className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Om synkronisering</p>
                  <p className="text-sm text-amber-700 mt-1">
                    • Kunder synkroniseras med namn, e-post, adress och org.nummer<br/>
                    • Fakturor exporteras med alla rader och moms<br/>
                    • Bokföringsverifikationer skapas automatiskt i Fortnox<br/>
                    • Synkroniseringen är enkelriktad (Yubi → Fortnox) för fakturor
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FortnoxIntegration;
